import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Cpu, Monitor, Gamepad2, Headphones, Zap, TrendingUp } from 'lucide-react';

const GamingTech: React.FC = () => {
  const techCards = [
    {
      id: 1,
      title: "Latest GPU Breakthroughs in 2025",
      description: "Discover how next-gen GPUs are reshaping 4K and VR gaming with unprecedented performance gains.",
      image: "https://images.unsplash.com/photo-1591799264318-7e6ef8ddb7ea?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      icon: <Cpu className="w-6 h-6" />,
      category: "Hardware"
    },
    {
      id: 2,
      title: "Revolutionary Gaming Monitors",
      description: "Explore ultra-wide OLED displays with 240Hz refresh rates that transform competitive gaming.",
      image: "https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      icon: <Monitor className="w-6 h-6" />,
      category: "Display"
    },
    {
      id: 3,
      title: "Next-Gen Console Technology",
      description: "Deep dive into the latest console innovations featuring ray tracing and SSD optimization.",
      image: "https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      icon: <Gamepad2 className="w-6 h-6" />,
      category: "Console"
    },
    {
      id: 4,
      title: "Immersive Audio Solutions",
      description: "Experience spatial audio technology that brings games to life with pinpoint accuracy.",
      image: "https://images.unsplash.com/photo-1484704849700-f032a568e944?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      icon: <Headphones className="w-6 h-6" />,
      category: "Audio"
    },
    {
      id: 5,
      title: "Lightning-Fast Storage Tech",
      description: "Explore NVMe SSDs that eliminate loading screens and enable instant game switching.",
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      icon: <Zap className="w-6 h-6" />,
      category: "Storage"
    },
    {
      id: 6,
      title: "AI-Powered Gaming Performance",
      description: "Learn how machine learning optimizes frame rates and enhances visual quality automatically.",
      image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      icon: <TrendingUp className="w-6 h-6" />,
      category: "AI"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="gaming-tech" className="py-20 bg-gradient-to-b from-dark-900 to-dark-800">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-futuristic font-bold mb-6">
            <span className="text-white">Gaming </span>
            <span className="bg-gradient-to-r from-primary-400 to-electric-500 bg-clip-text text-transparent">
              Technology
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Stay ahead of the curve with the latest breakthroughs in gaming hardware and technology
          </p>
        </motion.div>

        {/* Tech Cards Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {techCards.map((card) => (
            <motion.div
              key={card.id}
              variants={cardVariants}
              className="group glass rounded-xl overflow-hidden hover:border-primary-500/50 transition-all duration-300 cursor-pointer"
              whileHover={{ y: -10, scale: 1.02 }}
            >
              {/* Card Image */}
              <div className="relative overflow-hidden">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent" />
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="glass px-3 py-1 text-sm font-medium text-primary-400 rounded-full flex items-center space-x-1">
                    {card.icon}
                    <span>{card.category}</span>
                  </span>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-primary-400 transition-colors">
                  {card.title}
                </h3>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {card.description}
                </p>
                
                {/* Read More Button */}
                <motion.button
                  className="flex items-center space-x-2 text-primary-500 hover:text-primary-400 font-semibold group/btn"
                  whileHover={{ x: 5 }}
                >
                  <span>Read More</span>
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </motion.button>
              </div>

              {/* Hover Glow Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-electric-500/10 rounded-xl" />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Button */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <motion.button
            className="glass px-8 py-4 rounded-lg font-semibold text-white border-2 border-primary-500 hover:bg-primary-500 transition-all duration-300 flex items-center space-x-2 mx-auto"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>View All Tech Articles</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default GamingTech;
